import { Injectable } from '@angular/core';
import { Option, ReplyItem, Question } from '../../classes';
import { get, orderBy, minBy } from 'lodash';
import { PieChartComponent, BarHorizontalComponent } from '@swimlane/ngx-charts';
import JSZip from 'jszip';
import domtoimage from 'dom-to-image';
import { from, Observable, combineLatest, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { DatePipe } from '@angular/common';
import { PhoneNumberPipe } from '@schoolbelle/common/phone-number';
import { ExcelService } from '@schoolbelle/common/excel';
import { RepliesService } from '../../services';
import * as XLSX from 'xlsx';
import { PartialOption } from './charts.types';


@Injectable({
  providedIn: 'root'
})
export class ChartsService {
  private _data = new Map<string, PieChartComponent|BarHorizontalComponent>();
  constructor (
    protected replies:RepliesService,
    protected excel:ExcelService,
    protected datePipe:DatePipe,
    protected phoneNumberPipe:PhoneNumberPipe,    
  ) {

  }
  get (q_id) {
    return this._data.get(q_id);
  }
  set (q_id:string, chart:PieChartComponent|BarHorizontalComponent) {
    this._data.set(q_id, chart)
  }
  getColor (option:Option):string {
      return get(this.get(option.question.id), ['colors', 'colorDomain', ''+option.priority]);            
  }
  downloadCharts (elements:NodeList) {
    const elementlist = Array.from(elements);
    return combineLatest(...elementlist.map((el, i)=>this.chartToImageBlob(el)))
    .pipe(
      switchMap(bloblist=>{
        if (elementlist.length > 1) {
          const zip = new JSZip();
          bloblist.forEach((blob, i)=>zip.file(`chart(${i}).png`, blob));
          return zip.generateAsync({
            type:"blob",
            compression: "DEFLATE",
          })
        } else {
          return of(bloblist[0]);
        }
      }),
      tap(blob=>{
        if (elementlist.length > 1) saveAs(blob, "charts.zip");
        else saveAs(blob, `chart.png`);
      })
    )
  }    
  chartToImageBlob(el:Node):Observable<any>{
    return from(domtoimage.toBlob(el));
  }  
  downloadExcel (list:ReplyItem[],options:Array<PartialOption>, title:string ='list'):Observable<any> {
    if (options.length !== 1)  {
      const zip = new JSZip();
      options.forEach((o,i)=>{
          const sublist = orderBy(this.filterReplies(list, o), li=>this.getCheckTimestamp(li, o));
          const data = sublist.map((li, index)=>[
            '' + (index + 1),
            this.datePipe.transform(li.timestamp, 'MM-dd HH:mm:ss') || '', 
            li.displayName, 
            this.phoneNumberPipe.transform(li.phoneNumber) || '', 
            li.email, 
          ])
          const base64 = this.toExcelDataInBase64(data)
          zip.file(`${o.text}.xlsx`, base64, {base64:true});
      })
      return from(zip.generateAsync({
        type:"blob",
        compression: "DEFLATE",
      }))
      .pipe(
        tap(blob=>{
          saveAs(blob, `${title}.zip`);
        })
      )
    }
    else {
      const o = options[0];
      const sublist = orderBy(this.filterReplies(list, o), li=>this.getCheckTimestamp(li, o));
      const data = sublist.map((li, index)=>[
        '' + (index + 1),
        this.datePipe.transform(li.timestamp, 'MM-dd HH:mm:ss') || '', 
        li.displayName, 
        this.phoneNumberPipe.transform(li.phoneNumber) || '', 
        li.email, 
      ])      
      this.excel.download(data, `${o.text}`);
      return of(null);
    }
  }
  private toExcelDataInBase64 (data: Array<Array<string>>): void {
      /* generate worksheet */
      const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
  
      /* generate workbook and add the worksheet */
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  
      /* save to file */
      return XLSX.write(wb, {type:'base64'});
  }
  filterReplies (list:ReplyItem[], o:PartialOption) {
    const question_id = o.question.id, option_id = o.id;
    return list.filter(li=>get(li, ['snapshot', question_id, option_id]));
  }
  getCheckTimestamp (li:ReplyItem, o:PartialOption):number {
    const question_id = o.question.id, option_id = o.id, quota = o.quota;
    if (quota) return get(li, ['snapshot', question_id, option_id, 'timestamp']);
    else {
      if (question_id && option_id) {
        const list = this.replies.raw.filter(r=>r.as === li.as && get(r, ['snapshot', question_id, option_id]));
        return minBy(list, 'timestamp').timestamp;
      } else {
        return li.timestamp;
      }
    }  
  }  
}
